import React, { useState, useEffect, useRef } from "react";
import { PlusCircle, Trash2 } from "lucide-react";
import {
  FOREX_CARD_CURRENCY_OPTIONS,
  FOREX_CURRENCIES_NOTES_OPTIONS,
} from "pages/GenericOrder/steps/product-modal";
import { addCurrency } from "pages/GenericOrder/apis";
import { useMutation, useQueryClient } from "react-query";
import { formatInrMinor, inputToMinor } from "pages/GenericOrderDetailsPage/utils/money";

/** An indicative INR total for a row, worked in paise (the order itself is priced by core). */
function previewTotal(amount: string, rate: number): string {
  const minor = inputToMinor(amount);
  if (minor === null) return "";
  const rateMicros = BigInt(Math.round(rate * 1_000_000));
  return formatInrMinor(((minor * rateMicros + 500_000n) / 1_000_000n).toString());
}
import { toast } from "sonner";
import DeskDialog, { Field, inputClass } from "pages/GenericOrderDetailsPage/components/DeskDialog";
import { PartialAddError } from "service/moneeflo/legacy-forex-desk-api";
// Same signature, answered by the new backend. See legacy-order-api.ts.
import { getLiveRates } from "service/moneeflo/legacy-order-api";

interface AddCurrencyDialogProps {
  setIsCurrencyDialogOpen: (open: boolean) => void;
  prodcutType: string;
  selectedProductType: string;
  ProdcutList: any;
  /** An encashment's lines name no product; a sale's do. */
  isEncashment?: boolean;
}

interface CurrencyItem {
  id: number;
  currency: string;
  amount: string;
  rate: number;
  total: number;
}

const AddCurrencyDialog: React.FC<AddCurrencyDialogProps> = ({
  setIsCurrencyDialogOpen,
  prodcutType,
  selectedProductType,
  ProdcutList,
  isEncashment = false,
}) => {

  const queryClient = useQueryClient();
  const [rows, setRows] = useState<CurrencyItem[]>([]);
  const isInitialized = useRef(false);

  const finalCurrencyListOptions =
    selectedProductType === "Card"
      ? FOREX_CARD_CURRENCY_OPTIONS
      : FOREX_CURRENCIES_NOTES_OPTIONS;
  const finalCurrencyList = finalCurrencyListOptions.filter((currency) =>
    ProdcutList?.every((prod: any) => prod?.currencyWant !== currency.value)
  );

  // Fetch live rate for a given currency
  const fetchRate = async (currency: string) => {
    try {
      const res = await getLiveRates({
        currency: currency.toUpperCase(),
        type: prodcutType,
        fx_product: selectedProductType,
      });
      return Number(res?.rates) || 0;
    } catch (err) {
      toast.error("Failed to fetch rate");
      return 0;
    }
  };

  // Add a new currency row
  const handleAddRow = async (defaultCurrency?: string) => {
    let currency = defaultCurrency;

    if (!currency) {
      const availableCurrency = finalCurrencyList.find(
        (curr) => !rows.some((row) => row.currency === curr.value)
      );

      if (!availableCurrency) {
        toast.error("All available currencies have been added");
        return;
      }

      currency = availableCurrency.value;
    }

    const currencyExists = rows.some((row) => row.currency === currency);
    if (currencyExists) {
      toast.error(`${currency} is already added`);
      return;
    }

    const rate = await fetchRate(currency);

    setRows((prev) => [
      ...prev,
      { id: Date.now(), currency, amount: "", rate, total: 0 },
    ]);
  };

  // Update row when currency changes
  const handleCurrencyChange = async (id: number, newCurrency: string) => {
    const currencyExists = rows.some(
      (row) => row.id !== id && row.currency === newCurrency
    );
    if (currencyExists) {
      toast.error(`${newCurrency} is already added`);
      return;
    }

    const rate = await fetchRate(newCurrency);
    setRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
            ...row,
            currency: newCurrency,
            rate,
            total: parseFloat(
              ((parseFloat(row.amount) || 0) * rate).toFixed(4)
            ),
          }
          : row
      )
    );
  };

  // Update row when amount changes
  const handleAmountChange = (id: number, newAmount: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
            ...row,
            amount: newAmount,
            total: parseFloat(
              ((parseFloat(newAmount) || 0) * row.rate).toFixed(4)
            ),
          }
          : row
      )
    );
  };

  // Remove row
  const handleRemoveRow = (id: number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const canSave =
    rows.length > 0 &&
    rows.every((r) => parseFloat(r.amount) > 0 && r.rate > 0);

  const [error, setError] = useState<string | null>(null);

  // Mutation to save
  const mutation = useMutation({
    mutationFn: async () => {
      // The rate is core's own for each currency: only the amount goes. Core refuses a currency already on the order.
      return await addCurrency({
        lines: rows.map((row) => ({
          currency: row.currency,
          fxAmount: parseFloat(row.amount),
          productId: isEncashment ? undefined : selectedProductType === "Card" ? 16 : 9,
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["orderDetails"]);
      toast.success("Currency added");
      setIsCurrencyDialogOpen(false);
    },
    onError: (err) => {
      if (err instanceof PartialAddError) {
        // Some rows were saved: show the order as it is now and keep only the rows still to do.
        setRows((prev) => prev.filter((row) => !err.saved.includes(row.currency)));
        queryClient.invalidateQueries(["orderDetails"]);
      }
      setError(err instanceof Error ? err.message : "The currency could not be added.");
    },
  });

  useEffect(() => {
    if (!isInitialized.current && rows.length === 0) {
      isInitialized.current = true;
      const firstCurrency = finalCurrencyList[0]?.value || "USD";
      handleAddRow(firstCurrency);
    }
  }, []);

  return (
    <DeskDialog
      open
      onOpenChange={(open) => !open && setIsCurrencyDialogOpen(false)}
      title={`Add ${selectedProductType.toLowerCase()}`}
      description="Pick a currency and an amount. The rate is the system's."
      busy={mutation.isLoading}
      submitLabel="Save"
      submitDisabled={!canSave}
      closeLabel="Cancel"
      error={error}
      onSubmit={() => {
        setError(null);
        mutation.mutate();
      }}
    >
      {rows?.map((row, index) => (
        <div key={row?.id} className="flex flex-col gap-2 rounded-lg border border-[#E5E5E5] p-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Currency" htmlFor={`add-currency-${row.id}`}>
              <select
                id={`add-currency-${row.id}`}
                value={row?.currency}
                onChange={(e) => handleCurrencyChange(row?.id, e.target.value)}
                className={inputClass}
              >
                {finalCurrencyList.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Amount" htmlFor={`add-amount-${row.id}`}>
              <input
                id={`add-amount-${row.id}`}
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={row?.amount}
                onChange={(e) => handleAmountChange(row?.id, e.target.value)}
                className={inputClass}
                placeholder="Enter amount"
              />
            </Field>
          </div>
          <div className="flex items-center justify-between text-sm text-[#555555]">
            <span>
              {row?.amount ? `${row?.currency} ${row?.amount} × ₹${row?.rate} = ` : `Rate ₹${row?.rate}`}
              {row?.amount ? <span className="font-medium text-[#333333]">{previewTotal(row?.amount, row?.rate)}</span> : null}
            </span>
            {rows.length > 1 && (
              <button
                type="button"
                aria-label={`Remove row ${index + 1}`}
                onClick={() => handleRemoveRow(row.id)}
                className="cursor-pointer rounded p-1 text-[#B00020] hover:bg-[#FFF3F3]"
              >
                <Trash2 className="h-5 w-5" aria-hidden />
              </button>
            )}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => handleAddRow()}
        className="flex w-fit cursor-pointer items-center gap-1 text-sm font-medium text-[#165FE3] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        disabled={rows?.length >= finalCurrencyList?.length}
      >
        <PlusCircle size={16} aria-hidden /> Add another currency
      </button>
      {rows?.length >= finalCurrencyList?.length && (
        <p className="text-sm text-[#555555]">All available currencies have been added.</p>
      )}
    </DeskDialog>
  );
};

export default AddCurrencyDialog;
